import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Popconfirm, Popover, Space, Tag } from 'antd';
import React, { useRef, useState } from 'react';
import { queryKeys } from '../../services/react-query/queryKeys';
import {
  MdDelete,
  MdModeEditOutline,
  MdNoteAlt,
  MdSubtitles,
} from 'react-icons/md';
import Heading from '../../components/common/Heading/Heading';
import DynamicTableAnt from '../../hooks/DynamicTableAnt';
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import StatusTag from '../../hooks/StatusHook';
import { useNavigate } from 'react-router-dom';
import {
  deletePurchaseInvoice,
  getDebitNoteList,
  getPurchaseInvoice,
} from '../../network/purchaseInvoice';
import {
  cancelMaterialRequest,
  submitMaterialRequest,
} from '../../network/materialRequest';
import { BiShow } from 'react-icons/bi';
import { TiCancel } from 'react-icons/ti';
import PropsSearchTable from '../../components/common/PropsSearchTable/PropsSearchTable';
import { GiFiles } from 'react-icons/gi';
import { SiKnowledgebase } from 'react-icons/si';
import { PiDotsThreeVerticalBold } from 'react-icons/pi';

const DebitNote = ({ t }) => {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { message } = App.useApp();
  const searchInput = useRef(null);
  const searchInputMobile = useRef(null);
  const [textValue, setTextValue] = useState('');
  const [searchText, setSearchText] = useState({
    value: '',
    key: '',
  });
  const handleSearch = (selectedKeys, confirm, key) => {
    confirm();
    // console.log(`Searching for: ${selectedKeys[0]}, key: ${key}`);
    setSearchText({ value: selectedKeys[0], key });
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchText({ value: '', key: '' });
  };
  const props = ({ key, name }) =>
    PropsSearchTable({
      key: key,
      name: name,
      setTextValue: setTextValue,
      textValue: textValue,
      setSearchText: setSearchText,
      searchInputMobile: searchInputMobile,
      searchInput: searchInput,
      handleSearch: handleSearch,
      handleReset: handleReset,
      t: t,
    });
  const handleRemove = useMutation({
    mutationFn: (id) => deletePurchaseInvoice(id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500) {
        message.error(data?.message?.message);
        return false;
      }
      message.success(data?.message ? data?.message : 'Deleted successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getDebitNoteList] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const handleCancle = useMutation({
    mutationFn: (id) => cancelMaterialRequest('Purchase Invoice', id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500 || !data) {
        message.error(data?.message?.message || 'Error occured');
        return false;
      }
      message.success(data?.message?.message || 'Cancled successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getDebitNoteList] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const handleSubmit = useMutation({
    mutationFn: (id) => submitMaterialRequest('Purchase Invoice', id),
    onSuccess: (data) => {
      console.log(data, 'data');
      if (data?.message?.status === 500) {
        message.error(data?.message?.message);
        return false;
      }
      message.success(data?.message?.message || 'Submitted successfully');
      client.invalidateQueries({ queryKey: [queryKeys.getDebitNoteList] });
    },
    onError: () => {
      message.error('Something went wrong');
    },
  });
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.getDebitNoteList, searchText.value, searchText.key],
    queryFn: () =>
      getDebitNoteList({
        key: searchText.key,
        value: searchText.value,
      }),
    keepPreviousData: true,
  });
  const items = (record) => [
    {
      key: '1',
      label: t.view,
      icon: <BiShow />,
      onClick: () => {
        navigate(`/purchase-invoice/view/${record?.name}`);
      },
    },
    {
      key: '5',
      label: t.GeneralLedger,
      icon: <GiFiles />,
      onClick: () => {
        navigate(`/general-ledger-report-debit-note/${record?.name}`);
      },
    },
    {
      key: '2',
      label: t.submit,
      icon: <CheckOutlined />,
      onClick: () => {
        handleSubmit.mutate(record.name);
      },
      disabled: record?.docstatus !== 0,
    },

    {
      key: '8',
      label: (
        <Popconfirm
          title={t.CanceltheDebitNote}
          description={t.areYouSure}
          onConfirm={() => handleCancle.mutate(record.name)}
          // onCancel={cancel}
          okText={t.yes}
          cancelText={t.no}
        >
          <span style={{ color: 'red' }}>{t.Cancel}</span>
        </Popconfirm>
      ),
      icon: <TiCancel style={{ color: 'red' }} />,
    },
  ];
  const columns = [
    {
      title: t.Name,
      dataIndex: 'name',
      key: 'name',
      ...props({ key: 'name', name: t.Name }),
      render: (text, record) => {
        return (
          <span
            style={{ cursor: 'pointer' }}
            className="text-primary"
            onClick={() => {
              navigate(`/purchase-invoice/view/${record?.name}`);
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: t.supplierName,
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      ...props({ key: 'supplier_name', name: t.supplierName }),
    },
    {
      title: t.Total,
      dataIndex: 'grand_total',
      key: 'grand_total',
    },
    {
      title: t.status,
      dataIndex: 'status',
      key: 'status',
      ...props({ key: 'status', name: t.status }),
      render: (text, record) => <StatusTag status={record?.status} />,
    },
    {
      title: t.action,
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (text, record) => {
        return (
          <Space>
            {/* <Button
              size="small"
              type="primary"
              onClick={() => {
                navigate(`/purchase-invoice/view/${record?.name}`);
              }}
              icon={<BiShow />}
            />
            {record?.status === 'Draft' && (
              <Popover
                placement="top"
                title={t.submit}
                onClick={() => {
                  handleSubmit.mutate(record?.name);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#28a745' }}
                  icon={<MdSubtitles />}
                />
              </Popover>
            )}
            {record?.status !== 'Draft' && record?.status !== 'Cancelled' && (
              <Popover
                placement="top"
                title={t.Cancel}
                onClick={() => {
                  handleCancle.mutate(record?.name);
                }}
              >
                <Button
                  size="small"
                  type="primary"
                  style={{ backgroundColor: '#ff4d4f' }}
                  icon={<TiCancel />}
                />
              </Popover>
            )}
      
            <Popover
              placement="top"
              title={t.GeneralLedger}
              onClick={() => {
                navigate(`/general-ledger-report-debit-note/${record?.name}`);
              }}
            >
              <Button
                size="small"
                type="primary"
                icon={<GiFiles />}
                style={{
                  backgroundColor: '#c12f6e',
                }}
              />
            </Popover> */}
            <Dropdown
              menu={{ items: items(record) }}
              overlayClassName="custom-dropdown"
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<PiDotsThreeVerticalBold size={20} color="#86888C" />}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];
  const dataTable = data?.data?.map((item, index) => {
    return {
      key: index,
      ...item,
    };
  });
  return (
    <div className="inventory">
      <Heading title={t.Inventory} subTitle={t.purchaseInvoice} path="/" />
      <DynamicTableAnt
        columns={columns}
        data={dataTable}
        loading={isLoading}
        setSearchText={setSearchText}
        Placeholder={t.searchByName}
        buttonModal={null}
      />
    </div>
  );
};

export default DebitNote;
